const RICH_TEXT_FIELD_CANDIDATES = ['ISQUEMIAHTML', 'ISQUEMIA_HTML', 'LAUDOHTML', 'LAUDO_HTML'];

const ALLOWED_TAGS = new Set([
  'B',
  'BR',
  'DIV',
  'EM',
  'H4',
  'H5',
  'I',
  'LI',
  'OL',
  'P',
  'SPAN',
  'STRONG',
  'TABLE',
  'TBODY',
  'TD',
  'TH',
  'THEAD',
  'TR',
  'U',
  'UL',
]);

const ALLOWED_ATTRIBUTES = new Set(['colspan', 'rowspan', 'data-measure-row']);

const WINDOWS_1252_MAP = {
  0x80: '€',
  0x82: '‚',
  0x83: 'ƒ',
  0x84: '„',
  0x85: '…',
  0x86: '†',
  0x87: '‡',
  0x88: 'ˆ',
  0x89: '‰',
  0x8a: 'Š',
  0x8b: '‹',
  0x8c: 'Œ',
  0x8e: 'Ž',
  0x91: '‘',
  0x92: '’',
  0x93: '“',
  0x94: '”',
  0x95: '•',
  0x96: '–',
  0x97: '—',
  0x98: '˜',
  0x99: '™',
  0x9a: 'š',
  0x9b: '›',
  0x9c: 'œ',
  0x9e: 'ž',
  0x9f: 'Ÿ',
};

// Lista de cabeçalhos/tópicos conhecidos do laudo de stress
export const TOPICOS_CONHECIDOS = [
  'Avaliação da Função Sistólica do Ventrículo Esquerdo em repouso',
  'Avaliação da Função Diastólica do VE',
  'Avaliação do Ventrículo Esquerdo',
  'Comentários',
  'Átrio Esquerdo',
  'Átrio Direito',
  'Ventrículo Direito',
  'Aorta',
  'Valva Mitral',
  'Valva Aórtica',
  'Valva Tricúspide',
  'Valva Pulmonar',
  'Pericárdio',
  'Stress Fisiológico',
  'Stress Farmacológico',
  'CONCLUSÃO',
];

// Cria uma Regex dinâmica insensível a maiúsculas/minúsculas
export const regexTopicos = new RegExp(`^(${TOPICOS_CONHECIDOS.join('|')}):?`, 'i');

// Grupos de metadados do RTF que não pertencem ao corpo visível do laudo
const RTF_IGNORED_DESTINATIONS = new Set([
  'fonttbl',
  'colortbl',
  'stylesheet',
  'info',
  'pict',
  'object',
  'header',
  'footer',
  'headerl',
  'headerr',
  'footerl',
  'footerr',
  'footnote',
  'generator',
  'listtable',
  'listoverridetable',
  'rsidtbl',
  'mmathPr',
  'defchp',
  'defpap',
]);

function normalizeNewlines(value) {
  return String(value ?? '').replace(/\r\n?/g, '\n');
}

export function isLikelyRtf(value) {
  const raw = String(value ?? '').trimStart();
  return raw.startsWith('{\\rtf') || /\\(par|pard|fonttbl|colortbl|fs\d+|f\d+)\b/.test(raw);
}

function decodeRtfUnicode(codePoint) {
  const numeric = Number(codePoint);
  if (Number.isNaN(numeric)) return '';

  const resolved = numeric < 0 ? 65536 + numeric : numeric;
  if (resolved < 0 || resolved > 0x10ffff) return '';

  try {
    return String.fromCodePoint(resolved);
  } catch {
    return '';
  }
}

function decodeRtfHexByte(hex) {
  const code = Number.parseInt(hex, 16);
  if (Number.isNaN(code)) return '';

  if (WINDOWS_1252_MAP[code]) {
    return WINDOWS_1252_MAP[code];
  }

  return String.fromCharCode(code);
}

export function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Preserva múltiplos espaços e tabulações convertendo para espaços inquebráveis (&nbsp; / \u00A0),
 * fundamental para manter o alinhamento de colunas do sistema legado (ex: Septo....: ---   Via de Saída...: ---).
 */
export function preserveColumnSpacing(text) {
  return String(text ?? '')
    .replace(/\t/g, '\u00A0\u00A0\u00A0\u00A0')
    .replace(/ {2,}/g, (spaces) => `\u00A0${'\u00A0'.repeat(spaces.length - 1)}`);
}

/**
 * Detecta se uma linha possui duas colunas de medidas lado a lado
 * (ex: "Septo..............................: ---  (cm)       Via de Saída do VE........: ---")
 * e retorna [colunaEsquerda, colunaDireita] caso positivo, ou null caso contrário.
 */
export function splitMeasureColumns(lineText) {
  const normalized = String(lineText ?? '').replace(/\u00A0/g, ' ');
  const trimmed = normalized.trim();
  if (!trimmed) return null;

  // Padrão 1: Duas medidas separadas por 3+ espaços ou tab onde a segunda coluna também tem ":" ou ".."
  const matchTwoCols = trimmed.match(/^(.+?:.*?)(?:\t| {3,})([A-Za-zÀ-ÿ0-9].*(?:\.{2,}|:).*)$/);
  if (matchTwoCols) {
    return [matchTwoCols[1].trim(), matchTwoCols[2].trim()];
  }

  // Padrão 2: Linha solta de medida pontilhada na coluna esquerda (ex: "Diâmetro Sistólico.................: ---  (cm)")
  if (/^[^:]{2,38}\.{3,}\s*:/.test(trimmed)) {
    return [trimmed, ''];
  }

  return null;
}

/**
 * Parser determinístico de RTF (Delphi RichEdit) para lista de parágrafos ricos
 * contendo runs estilizados ({ text, bold, italic, underline }).
 * Remove completamente grupos de metadados ({\fonttbl...}, {\colortbl...}, {\*\...}).
 */
function parseRtfToParagraphs(rtfText) {
  const src = normalizeNewlines(rtfText);
  const length = src.length;

  let i = 0;
  let ucSkip = 1;
  const stateStack = [];
  let state = {
    bold: false,
    italic: false,
    underline: false,
    ignore: false,
  };

  const paragraphs = [[]];

  const pushText = (text) => {
    if (!text || state.ignore) return;
    const currentParagraph = paragraphs[paragraphs.length - 1];
    const prevRun = currentParagraph[currentParagraph.length - 1];

    if (
      prevRun &&
      prevRun.bold === state.bold &&
      prevRun.italic === state.italic &&
      prevRun.underline === state.underline
    ) {
      prevRun.text += text;
    } else {
      currentParagraph.push({
        text,
        bold: state.bold,
        italic: state.italic,
        underline: state.underline,
      });
    }
  };

  const startNewParagraph = () => {
    if (state.ignore) return;
    paragraphs.push([]);
  };

  while (i < length) {
    const ch = src[i];

    if (ch === '{') {
      stateStack.push({ ...state });
      i += 1;
      // Verifica se é grupo de destino opcional {\*\...}
      if (src[i] === '\\' && src[i + 1] === '*') {
        state.ignore = true;
        i += 2;
      }
      continue;
    }

    if (ch === '}') {
      if (stateStack.length > 0) {
        state = stateStack.pop();
      }
      i += 1;
      continue;
    }

    if (ch === '\n' || ch === '\r') {
      i += 1;
      continue;
    }

    if (ch === '\\') {
      i += 1;
      if (i >= length) break;

      const next = src[i];

      // Caracteres escapados literais
      if (next === '\\' || next === '{' || next === '}') {
        pushText(next);
        i += 1;
        continue;
      }

      if (next === '~') {
        pushText('\u00A0');
        i += 1;
        continue;
      }

      if (next === '_') {
        pushText('-');
        i += 1;
        continue;
      }

      if (next === '\n' || next === '\r') {
        startNewParagraph();
        i += 1;
        continue;
      }

      // Hex escape \'xx
      if (next === "'") {
        const hex = src.slice(i + 1, i + 3);
        if (/^[0-9a-fA-F]{2}$/.test(hex)) {
          pushText(decodeRtfHexByte(hex));
          i += 3;
          continue;
        }
        i += 1;
        continue;
      }

      // Ignorar símbolo de destino opcional se aparecer solto
      if (next === '*') {
        state.ignore = true;
        i += 1;
        continue;
      }

      // Lê palavra de controle RTF: \<word><optional signed number><optional space>
      const match = src.slice(i).match(/^([a-zA-Z]+)(-?\d*) ?/);
      if (match) {
        const fullMatch = match[0];
        const word = match[1].toLowerCase();
        const paramStr = match[2];
        const param = paramStr !== '' ? Number.parseInt(paramStr, 10) : null;

        i += fullMatch.length;

        if (RTF_IGNORED_DESTINATIONS.has(word)) {
          state.ignore = true;
          continue;
        }

        if (state.ignore) {
          continue;
        }

        switch (word) {
          case 'par':
          case 'line':
            startNewParagraph();
            break;
          case 'pard':
          case 'plain':
            state.bold = false;
            state.italic = false;
            state.underline = false;
            break;
          case 'b':
            state.bold = param !== 0;
            break;
          case 'i':
            state.italic = param !== 0;
            break;
          case 'ul':
            state.underline = param !== 0;
            break;
          case 'ulnone':
            state.underline = false;
            break;
          case 'tab':
            pushText('\t');
            break;
          case 'uc':
            if (param !== null && !Number.isNaN(param) && param >= 0) {
              ucSkip = param;
            }
            break;
          case 'u': {
            if (param !== null) {
              pushText(decodeRtfUnicode(param));
              // Pula o caractere substituto ANSI após \uN (normalmente '?')
              let skipped = 0;
              while (skipped < ucSkip && i < length) {
                if (src[i] === '\\' && src[i + 1] === "'") {
                  i += 4;
                } else if (src[i] !== '{' && src[i] !== '}') {
                  i += 1;
                } else {
                  break;
                }
                skipped += 1;
              }
            }
            break;
          }
          default:
            break;
        }
        continue;
      }

      // Qualquer outro caractere de controle desconhecido
      i += 1;
      continue;
    }

    pushText(ch);
    i += 1;
  }

  return paragraphs;
}

/**
 * Limpa resíduos de tabelas de fontes/cores caso o backend tenha quebrado o RTF em linhas
 * antes de enviar para o frontend.
 */
function cleanBrokenRtfArtifacts(line) {
  let cleaned = String(line ?? '');

  // Remove trechos típicos de fonttbl/colortbl do Delphi caso tenham vazado em linhas quebradas
  cleaned = cleaned
    .replace(/\{\\fonttbl[^}]*\}/gi, '')
    .replace(/\{\\colortbl[^}]*\}/gi, '')
    .replace(/^(?:\\f\d+\\fnil\\fcharset\d+\s+[^;]+;)+/gi, '')
    .replace(/^(?:MS Sans Serif|Arial|Times New Roman|Courier New|Symbol|Tahoma);+/gi, '')
    .replace(/^(?:\\red\d+\\green\d+\\blue\d+;)+/gi, '');

  return cleaned;
}

export function decodeLegacyRtf(rtfText) {
  if (!rtfText) return '';

  const normalized = normalizeNewlines(rtfText);
  if (!isLikelyRtf(normalized)) {
    return normalized
      .split('\n')
      .map((line) => cleanBrokenRtfArtifacts(line).replace(/[ \t]+$/g, ''))
      .join('\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  const paragraphs = parseRtfToParagraphs(normalized);
  const lines = paragraphs.map((runs) =>
    runs
      .map((run) => run.text)
      .join('')
      .replace(/\u0000/g, '')
      .replace(/[ \t]+$/g, '')
  );

  return lines
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function renderRunToHtml(run) {
  if (!run.text) return '';
  let content = escapeHtml(preserveColumnSpacing(run.text));

  if (run.underline) {
    content = `<u>${content}</u>`;
  }
  if (run.italic) {
    content = `<em>${content}</em>`;
  }
  if (run.bold) {
    content = `<strong>${content}</strong>`;
  }
  return content;
}

/**
 * Converte RTF do sistema legado diretamente para HTML rico,
 * preservando negrito (\b), itálico (\i), sublinhado (\ul) e alinhamento de colunas.
 */
export function rtfToStressHtml(rtfText) {
  if (!rtfText) return '';

  const normalized = normalizeNewlines(rtfText);
  if (!isLikelyRtf(normalized)) {
    return plainTextToStressHtml(normalized);
  }

  const paragraphs = parseRtfToParagraphs(normalized);

  // Remove parágrafos vazios iniciais e finais gerados pelo cabeçalho RTF
  while (
    paragraphs.length > 0 &&
    paragraphs[0]
      .map((r) => r.text)
      .join('')
      .trim() === ''
  ) {
    paragraphs.shift();
  }
  while (
    paragraphs.length > 0 &&
    paragraphs[paragraphs.length - 1]
      .map((r) => r.text)
      .join('')
      .trim() === ''
  ) {
    paragraphs.pop();
  }

  const htmlLines = paragraphs.map((runs) => {
    const rawLine = runs
      .map((r) => r.text)
      .join('')
      .replace(/\u0000/g, '')
      .replace(/[ \t]+$/g, '');

    if (!rawLine.trim()) {
      return '<p><br></p>';
    }

    const hasExplicitFormatting = runs.some((r) => r.bold || r.italic || r.underline);
    if (hasExplicitFormatting) {
      const innerHtml = runs.map(renderRunToHtml).join('');
      return `<p>${innerHtml}</p>`;
    }

    // Se o parágrafo não tinha marcação explícita de negrito no RTF, aplica heurística de tópicos/colunas
    return formatPlainLineToHtml(rawLine);
  });

  return htmlLines.join('');
}

function formatPlainLineToHtml(linha) {
  const linhaSemLixo = cleanBrokenRtfArtifacts(linha).replace(/[ \t]+$/g, '');
  const linhaTrimmed = linhaSemLixo.trim();

  if (!linhaTrimmed) {
    return '<p><br></p>';
  }

  const linhaComEspacos = preserveColumnSpacing(linhaSemLixo);

  // Regra 1: Casamento com a lista de tópicos conhecidos
  const matchTopico = linhaTrimmed.match(regexTopicos);
  if (matchTopico) {
    const topicoEncontrado = matchTopico[0];
    const restoDaLinha = linhaTrimmed.slice(topicoEncontrado.length);
    return `<p><strong>${escapeHtml(topicoEncontrado)}</strong>${escapeHtml(preserveColumnSpacing(restoDaLinha))}</p>`;
  }

  // Regra 2: Heurística genérica - Qualquer linha curta que termine com ":" vira cabeçalho
  if (linhaTrimmed.endsWith(':') && linhaTrimmed.length < 65 && !linhaTrimmed.includes('..')) {
    return `<p><strong>${escapeHtml(linhaTrimmed)}</strong></p>`;
  }

  // Regra 3: Linha normal preservando espaços de coluna
  return `<p>${escapeHtml(linhaComEspacos)}</p>`;
}

/**
 * Converte o texto puro do laudo para HTML formatado para edição e impressão.
 * @param {string} plainText
 * @returns {string} HTML final com negrito nos tópicos e preservação de colunas
 */
export function plainTextToStressHtml(plainText) {
  if (!plainText) return '';

  const normalized = normalizeNewlines(plainText);
  if (isLikelyRtf(normalized)) {
    return rtfToStressHtml(normalized);
  }

  const linhas = normalized.split('\n');
  return linhas.map(formatPlainLineToHtml).join('');
}

function serializeNode(node) {
  if (!node) return '';

  if (node.nodeType === Node.TEXT_NODE) {
    return escapeHtml(node.textContent);
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return '';
  }

  const tagName = node.tagName.toUpperCase();
  if (!ALLOWED_TAGS.has(tagName)) {
    return Array.from(node.childNodes).map(serializeNode).join('');
  }

  // Normaliza B -> STRONG e I -> EM para manter HTML consistente
  const normalizedTag = tagName === 'B' ? 'strong' : tagName === 'I' ? 'em' : tagName.toLowerCase();

  const attrs = Array.from(node.attributes || [])
    .filter((attribute) => ALLOWED_ATTRIBUTES.has(attribute.name.toLowerCase()))
    .map((attribute) => ` ${attribute.name.toLowerCase()}="${escapeHtml(attribute.value)}"`)
    .join('');

  if (tagName === 'BR') {
    return '<br>';
  }

  const children = Array.from(node.childNodes).map(serializeNode).join('');
  return `<${normalizedTag}${attrs}>${children}</${normalizedTag}>`;
}

function collectNodeText(node) {
  if (!node) return '';

  if (node.nodeType === Node.TEXT_NODE) {
    return (node.textContent || '').replace(/\u00A0/g, ' ');
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return '';
  }

  const tagName = node.tagName.toUpperCase();

  if (tagName === 'BR') return '\n';

  if (tagName === 'TR') {
    const cells = Array.from(node.children || []).map((child) =>
      collectNodeText(child).replace(/\n+/g, ' ').trim()
    );
    const nonEmptyCells = cells.filter(Boolean);
    if (nonEmptyCells.length === 2) {
      return `${nonEmptyCells[0].padEnd(48, ' ')}${nonEmptyCells[1]}\n`;
    }
    return `${nonEmptyCells.join('    ')}\n`;
  }

  if (tagName === 'LI') {
    return `- ${Array.from(node.childNodes).map(collectNodeText).join('').trim()}\n`;
  }

  const childText = Array.from(node.childNodes).map(collectNodeText).join('');

  if (['DIV', 'H4', 'H5', 'OL', 'P', 'TABLE', 'TBODY', 'THEAD', 'UL'].includes(tagName)) {
    return `${childText}\n`;
  }

  return childText;
}

export function detectStressRichTextField(stressData) {
  if (!stressData) return '';

  return (
    RICH_TEXT_FIELD_CANDIDATES.find((field) =>
      Object.prototype.hasOwnProperty.call(stressData, field)
    ) || ''
  );
}

export function extractStressLegacyText(stressData) {
  const rawValue = stressData?.ISQUEMIA;
  const mergedRaw = Array.isArray(rawValue)
    ? rawValue.map((item) => String(item ?? '')).join('\n')
    : String(rawValue ?? '');

  return decodeLegacyRtf(mergedRaw);
}

export function isStressLegacyRtf(stressData) {
  const rawValue = stressData?.ISQUEMIA;

  if (Array.isArray(rawValue)) {
    return rawValue.some((item) => isLikelyRtf(item));
  }

  return isLikelyRtf(String(rawValue ?? ''));
}

export function sanitizeStressHtml(html) {
  const normalized = String(html ?? '').trim();
  if (!normalized) return '';

  const parser = new DOMParser();
  const doc = parser.parseFromString(normalized, 'text/html');

  return Array.from(doc.body.childNodes).map(serializeNode).join('');
}

export function htmlToStressPlainText(html) {
  const sanitizedHtml = sanitizeStressHtml(html);
  if (!sanitizedHtml) return '';

  const parser = new DOMParser();
  const doc = parser.parseFromString(sanitizedHtml, 'text/html');
  const text = Array.from(doc.body.childNodes).map(collectNodeText).join('');

  return normalizeNewlines(text)
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function getStressRichTextContent(stressData) {
  if (!stressData) return '';

  const richField = detectStressRichTextField(stressData);
  const richValue = richField ? stressData?.[richField] : '';

  if (String(richValue ?? '').trim()) {
    return sanitizeStressHtml(richValue);
  }

  const rawIsquemia = stressData?.ISQUEMIA;
  const mergedRaw = Array.isArray(rawIsquemia)
    ? rawIsquemia.map((item) => String(item ?? '')).join('\n')
    : String(rawIsquemia ?? '');

  if (isLikelyRtf(mergedRaw)) {
    return sanitizeStressHtml(rtfToStressHtml(mergedRaw));
  }

  return sanitizeStressHtml(plainTextToStressHtml(decodeLegacyRtf(mergedRaw)));
}

export function hasStoredStressRichText(stressData) {
  const richField = detectStressRichTextField(stressData);
  return Boolean(richField && String(stressData?.[richField] ?? '').trim());
}

export function hasStressDocumentContent(html) {
  return Boolean(htmlToStressPlainText(html).trim());
}

export function buildStressPayload({ tipoStress, htmlContent, stressData }) {
  const sanitizedHtml = sanitizeStressHtml(htmlContent);

  return {
    tipoStress,
    ISQUEMIAHTML: sanitizedHtml,
    codFuncLaudo: stressData?.CODFUNCLAUDO,
  };
}
